<template lang="pug">
form.section.content(v-on:submit.prevent="submit")
  div.missing(v-if="hasInvalidProfessionalLocations || hasInvalidProfessionalPrices || hasInvalidProfessionalStatus") Your profile is missing:
    span(v-if="hasInvalidProfessionalLocations")
      | &nbsp;
      .tag.is-warning.is-medium a location
    span(v-if="hasInvalidProfessionalPrices")
      | &nbsp;
      .tag.is-warning.is-medium at least one price
    span(v-if="hasInvalidProfessionalStatus")
      | &nbsp;
      .tag.is-warning.is-medium a verified email
  .field
    .control.is-expanded
      b-upload(accept="image/*" drag-drop v-model="avatarFile" v-on:input="uploadFile")
        template(v-if="avatar")
          .image.is-128x128
            img(v-bind:src="avatar")
        .icon
          i.fas.fa-upload
        span Upload Image
  .field
    .label Email
    .level
      .level-left
        .level-item
          span {{ email }}
        .level-item
          .button.is-warning(v-if="hasInvalidProfessionalStatus" v-on:click="resendVerificationEmail") Resend verification email
  .field
    .label Full Name
    .control.is-expanded
      input.input(placeholder="Name" ref="focus" v-bind:class="[$v.name.$error ? 'is-danger' : 'is-success']" v-model="$v.name.$model")
    .help.is-danger(v-show="$v.name.$error && !$v.name.required") Name is required
  .field
    .label Phone Number
    .control.is-expanded
      input.input(placeholder="Phone" v-bind:class="[$v.phone.$error ? 'is-danger' : 'is-success']" v-model="$v.phone.$model")
    .help.is-danger(v-show="$v.phone.$error && !$v.phone.validPhone") Phone is invalid
  .field
    .label Date of Birth
    .control
      b-datepicker(placeholder="Date of Birth" v-bind:class="[$v.dob.$error ? 'is-danger' : 'is-success']" v-bind:maxDate="maxDateOfBirth" v-bind:minDate="minDateOfBirth" v-model="$v.dob.$model")
    .help.is-danger(v-show="$v.dob.$error && !$v.dob.tooRecent") Date of birth is too recent
  .field
    .label Gender
    .block
      b-radio(name="gender" native-value="F" v-model="$v.gender.$model") Female
      b-radio(name="gender" native-value="M" v-model="$v.gender.$model") Male
      b-radio(name="gender" native-value="P" v-model="$v.gender.$model") Prefer not to say
  .field
    .label Are you insured?
    .block
      b-radio(name="insurance" native-value="0" v-model="$v.insurance.$model") No
      b-radio(name="insurance" native-value="1" v-model="$v.insurance.$model") Yes
  .field
    .label Headline
    .control
      input.input(placeholder="Headline" v-bind:class="[$v.headline.$error ? 'is-danger' : 'is-success']" v-model="$v.headline.$model")
    .help.is-danger(v-show="$v.headline.$error && !$v.headline.maxLength") Headline is too long
  .field
    .label About You
    .control
      input.input(placeholder="About" v-bind:class="[$v.description.$error ? 'is-danger' : 'is-success']" v-model="$v.description.$model")
  .field
    .label Location Description
    .control
      input.input(placeholder="Location Description" v-bind:class="[$v.locationDescription.$error ? 'is-danger' : 'is-success']" v-model="$v.locationDescription.$model")
  .field
    .label Where do you work?
    div(v-for="location in locations")
      span(v-on:click="clickDeleteLocation(location.location.id)")
        b-icon(icon="times" size="is-small" type="is-danger")
      span {{ isOperatorWithGyms ? location.location.name : location.location.address }}
  .field(v-if="isOperatorWithGyms")
    .control
      .select
        select(v-model="selectLocation" v-on:change="clickInsertLocation")
          option(value="0") {{ selectALocationLabel }}
          option(v-bind:value="location.id" v-for="location in insertableLocations") {{ location.name }}
  .field(v-if="isOperatorWithLocations")
    .control
      .button.is-outlined.is-primary(v-on:click="clickNewLocation") New Location
  .field
    .label Price 1
    .control
      input.input(max="999" min="0" placeholder="Price 1" step="1" type="number" v-bind:class="[$v.price1.$error ? 'is-danger' : 'is-success']" v-model.number="$v.price1.$model")
    .help.is-danger(v-show="$v.price1.$error && !$v.price1.required") Price 1 is required
    .help.is-danger(v-show="$v.price1.$error && !$v.price1.minValue") Price 1 is too small
  .field
    .label Price 5
    .control
      input.input(max="999" min="0" placeholder="Price 5" step="1" type="number" v-bind:class="[$v.price5.$error ? 'is-danger' : 'is-success']" v-model.number="$v.price5.$model")
    .help.is-danger(v-show="$v.price5.$error && !$v.price5.required") Price 5 is required
    .help.is-danger(v-show="$v.price5.$error && !$v.price5.minValue") Price 5 is too small
  .field
    .label Price 10
    .control
      input.input(max="999" min="0" placeholder="Price 10" step="1" type="number" v-bind:class="[$v.price10.$error ? 'is-danger' : 'is-success']" v-model.number="$v.price10.$model")
    .help.is-danger(v-show="$v.price10.$error && !$v.price10.required") Price 10 is required
    .help.is-danger(v-show="$v.price10.$error && !$v.price10.minValue") Price 10 is too small
  .field
    .label Associations
    div(v-for="membership in memberships")
      span(v-on:click="clickDeleteMembership(membership.id)")
        b-icon(icon="times" size="is-small" type="is-danger")
      span {{ membership.name }}
    .control
      .select
        select(v-model="selectMembership" v-on:change="clickInsertMembership")
          option(value="0") Select an Association
          option(v-bind:value="membership.id" v-for="membership in insertableMemberships") {{ membership.name }}
  .field
    .label Qualifications
    div(v-for="qualification in qualifications")
      span(v-on:click="clickDeleteQualification(qualification.id)")
        b-icon(icon="times" size="is-small" type="is-danger")
      span {{ qualification.name }}
    .control
      .select
        select(v-model="selectQualification" v-on:change="clickInsertQualification")
          option(value="0") Select a Qualification
          option(v-bind:value="qualification.id" v-for="qualification in insertableQualifications") {{ qualification.name }}
  .field
    .label Services
    div(v-for="profession in professions")
      span(v-on:click="clickDeleteProfession(profession.id)")
        b-icon(icon="times" size="is-small" type="is-danger")
      span {{ profession.name }}
    .control
      .select
        select(v-model="selectProfession" v-on:change="clickInsertProfession")
          option(value="0") Select a Service
          option(v-bind:value="profession.id" v-for="profession in insertableProfessions") {{ profession.name }}
  .field
    .label Specialisms
    div(v-for="service in services")
      span(v-on:click="clickDeleteService(service.id)")
        b-icon(icon="times" size="is-small" type="is-danger")
      span {{ service.name }}
    .control
      .select
        select(v-model="selectService" v-on:change="clickInsertService")
          option(value="0") Select a Specialism
          option(v-bind:value="services.id" v-for="services in insertableServices") {{ services.name }}
  .field
    .control
      button.button.is-fullwidth.is-primary(type="submit" v-bind:disabled="$v.$invalid") Save
</template>

<script>
import { isBefore, parse, subYears } from 'date-fns'
import { email, helpers, minValue, required } from 'vuelidate/lib/validators'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'

import Constants from '@/constants'

import EnterNewLocationForm from '@/components/EnterNewLocationForm.vue'

import ApiMixin from '@/mixins/ApiMixin.vue'
import DeviceMixin from '@/mixins/DeviceMixin.vue'

const validName = helpers.regex('invalid', /^.+ .+$/)
const validTagline = (tagline) => !helpers.req(tagline) || encodeURI(tagline).length <= 150

export default {
  mixins: [
    ApiMixin,
    DeviceMixin
  ],
  data () {
    return {
      apiMemberships: [],
      apiQualifications: [],
      avatar: null,
      avatarFile: null,
      description: null,
      dob: null,
      email: null,
      gender: null,
      headline: null,
      id: null,
      initialLocations: [],
      insurance: null,
      locationDescription: null,
      locations: [],
      memberships: [],
      name: null,
      phone: null,
      price1: null,
      price5: null,
      price10: null,
      professions: [],
      qualifications: [],
      selectLocation: 0,
      selectMembership: 0,
      selectProfession: 0,
      selectQualification: 0,
      selectService: 0,
      services: []
    }
  },
  computed: {
    insertableLocations () {
      return this.apiLocations.filter((location) => location.site_ref !== null).filter((location) => !this.locations.map((location) => location.location.id).includes(location.id)).sort((a, b) => a.name.localeCompare(b.name)).filter((location) => location.name.startsWith(this.operatorName))
    },
    insertableMemberships () {
      return this.apiMemberships.filter((membership) => !this.memberships.includes(membership)).sort((a, b) => a.name.localeCompare(b.name))
    },
    insertableProfessions () {
      return this.apiProfessions.filter((profession) => !this.professions.includes(profession)).sort((a, b) => a.name.localeCompare(b.name))
    },
    insertableQualifications () {
      return this.apiQualifications.filter((qualification) => !this.qualifications.includes(qualification)).sort((a, b) => a.name.localeCompare(b.name))
    },
    insertableServices () {
      return this.apiServices.filter((service) => !this.services.includes(service)).sort((a, b) => a.name.localeCompare(b.name))
    },
    locationsToDelete () {
      return this.apiProfessional.locations.map((location) => location.id).filter((locationId) => !this.locations.map((location) => location.location.id).includes(locationId))
    },
    locationsToInsert () {
      if (this.isOperatorWithGyms) {
        return this.locations.filter((location) => !location.location.id || !this.initialLocations.map((initialLocation) => initialLocation.location.id).includes(location.location.id))
      } else {
        return this.locations.filter((location) => !location.location.id)
      }
    },
    maxDateOfBirth () {
      return subYears(new Date(), Constants.DATE_OF_BIRTH_MIN_AGE)
    },
    membershipsToDelete () {
      const initialMemberships = this.apiMemberships.filter((membership) => this.apiProfessional.memberships.includes(membership.id))
      return initialMemberships.filter((initialMembership) => !this.memberships.map((membership) => membership.name).includes(initialMembership.name))
    },
    membershipsToInsert () {
      return this.memberships.filter((membership) => !this.apiProfessional.memberships.includes(membership.id))
    },
    minDateOfBirth () {
      return subYears(new Date(), Constants.DATE_OF_BIRTH_MIN_YEAR)
    },
    professionsToDelete () {
      const initialProfessions = this.apiProfessions.filter((profession) => this.apiProfessional.professions.includes(profession.id))
      return initialProfessions.filter((initialProfession) => !this.professions.map((profession) => profession.name).includes(initialProfession.name))
    },
    professionsToInsert () {
      return this.professions.filter((profession) => !this.apiProfessional.professions.includes(profession.id))
    },
    qualificationsToDelete () {
      const initialQualifications = this.apiQualifications.filter((qualification) => this.apiProfessional.qualifications.includes(qualification.id))
      return initialQualifications.filter((initialQualification) => !this.qualifications.map((qualification) => qualification.name).includes(initialQualification.name))
    },
    qualificationsToInsert () {
      return this.qualifications.filter((qualification) => !this.apiProfessional.qualifications.includes(qualification.id))
    },
    selectALocationLabel () {
      return `Select a ${this.isOperatorWithGyms ? this.operatorName : ''} Location`
    },
    servicesToDelete () {
      const initialServices = this.apiServices.filter((service) => this.apiProfessional.services.includes(service.id))
      return initialServices.filter((initialService) => !this.services.map((service) => service.name).includes(initialService.name))
    },
    servicesToInsert () {
      return this.services.filter((service) => !this.apiProfessional.services.includes(service.id))
    }
  },
  async mounted () {
    await this.mountedImpl()
  },
  methods: {
    clickDeleteLocation (toDelete) {
      this.locations = this.locations.filter((location) => location.location.id !== toDelete)
    },
    clickDeleteMembership (toDelete) {
      this.memberships = this.memberships.filter((membership) => membership.id !== toDelete)
    },
    clickDeleteProfession (toDelete) {
      this.professions = this.professions.filter((profession) => profession.id !== toDelete)
    },
    clickDeleteQualification (toDelete) {
      this.qualifications = this.qualifications.filter((qualification) => qualification.id !== toDelete)
    },
    clickDeleteService (toDelete) {
      this.services = this.services.filter((service) => service.id !== toDelete)
    },
    clickInsertLocation () {
      this.locations.push({
        location: this.apiLocations.find((location) => location.id === this.selectLocation),
        name: 'Work',
        professional_id: this.initialId,
        range: 0
      })
      this.selectLocation = 0
    },
    clickInsertMembership () {
      this.memberships.push(this.apiMemberships.find((membership) => membership.id === this.selectMembership))
      this.selectMembership = 0
    },
    clickInsertProfession () {
      this.professions.push(this.apiProfessions.find((profession) => profession.id === this.selectProfession))
      this.selectProfession = 0
    },
    clickInsertQualification () {
      this.qualifications.push(this.apiQualifications.find((qualification) => qualification.id === this.selectQualification))
      this.selectQualification = 0
    },
    clickInsertService () {
      this.services.push(this.apiServices.find((service) => service.id === this.selectService))
      this.selectService = 0
    },
    clickNewLocation () {
      this.$buefy.modal.open({
        component: EnterNewLocationForm,
        hasModalCard: true,
        parent: this,
        props: {
          impl: this.enterNewLocationFormImpl,
          initialLocation: '',
          noun: 'New Location',
          showName: true,
          showRange: true,
          showSave: false,
          verbBase: 'Enter',
          verbGerund: 'Entering'
        }
      })
    },
    async enterNewLocationFormImpl (name, place, range) {
      const location = {
        address: place.formatted_address,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        name: place.name,
        postcode: place.address_components.filter((addressComponent) => addressComponent.types.includes('postal_code')).reduce((accumulator, addressComponent) => addressComponent.long_name, null)
      }
      this.locations.push({
        location,
        name,
        professional_id: this.apiProfessional.id,
        range
      })
    },
    async mapLocations () {
      const locations = []
      const promises = this.apiProfessional.locations.map(async (location) => {
        const json = await this.apiGetProfessionalLocation(this.apiProfessional.id, location.id)
        locations.push(json)
      })
      await Promise.all(promises)
      return locations.sort((a, b) => a.location.name.localeCompare(b.location.name))
    },
    async mountedImpl () {
      this.deviceSetFocus()

      await this.apiGetAccreditations()
      await this.apiGetLocations()
      await this.apiGetProfessions()
      await this.apiGetServices()

      this.apiMemberships = this.apiAccreditations.filter((accreditation) => accreditation.type === 'membership')
      this.apiQualifications = this.apiAccreditations.filter((accreditation) => accreditation.type === 'qualification')

      this.avatar = this.apiProfessional.avatar?.url
      this.description = decodeURIComponent(this.apiProfessional.description)
      this.dob = parse(this.apiProfessional.dob, 'YYYY-MM-DD')
      this.email = this.apiProfessional.email
      this.gender = this.apiProfessional.gender
      this.headline = decodeURIComponent(this.apiProfessional.tagline)
      this.id = this.apiProfessional.id
      this.initialLocations = await this.mapLocations()
      this.insurance = this.apiProfessional.insurance
      this.locationDescription = decodeURIComponent(this.apiProfessional.location_description)
      this.locations = await this.mapLocations()
      this.memberships = this.apiMemberships.filter((membership) => this.apiProfessional.memberships.includes(membership.id))
      this.name = this.apiProfessional.name
      this.phone = this.apiProfessional.phone
      this.price1 = this.apiProfessional.prices[0]?.General.price1
      this.price5 = this.apiProfessional.prices[0]?.General.price5
      this.price10 = this.apiProfessional.prices[0]?.General.price10
      this.professions = this.apiProfessions.filter((profession) => this.apiProfessional.professions.includes(profession.id))
      this.qualifications = this.apiQualifications.filter((qualification) => this.apiProfessional.qualifications.includes(qualification.id))
      this.services = this.apiServices.filter((service) => this.apiProfessional.services.includes(service.id))
    },
    async resendVerificationEmail () {
      await this.apiResendVerificationEmail()
    },
    async submit () {
      await this.apiUpdateProfessional(this.apiProfessional.id, this.avatar, this.description, this.dob, this.gender, this.headline, this.insurance, this.locationDescription, this.locationsToDelete, this.locationsToInsert, this.membershipsToDelete, this.membershipsToInsert, this.name, this.phone, this.price1, this.price5, this.price10, this.professionsToDelete, this.professionsToInsert, this.qualificationsToDelete, this.qualificationsToInsert, this.servicesToDelete, this.servicesToInsert)
      await this.mountedImpl()
    },
    uploadFile (file) {
      if (file) {
        if (file.size < 5120 * 5120) {
          const fileReader = new FileReader()
          fileReader.addEventListener('load', () => {
            try {
              this.avatar = atob(btoa(fileReader.result))
            } catch (error) {
              this.alertDisplayFailure(error)
            }
          })
          fileReader.readAsDataURL(file)
        } else {
          this.alertDisplayFailure('The chosen file is too big.')
        }
      }
    }
  },
  validations () {
    const price5Validations = {
      price5: {
        minValue: minValue(this.price1),
        required
      }
    }
    const price5price10Validations = {
      price10: {
        minValue: minValue(this.price5),
        required
      }
    }
    const price10Validations = {
      price10: {
        minValue: minValue(this.price1),
        required
      }
    }
    let validations = {
      description: {},
      dob: {
        tooRecent (string) {
          return isBefore(new Date(string), subYears(new Date(), Constants.DATE_OF_BIRTH_MIN_AGE))
        }
      },
      email: {
        email,
        required
      },
      gender: {},
      headline: {
        validTagline
      },
      insurance: {},
      locationDescription: {},
      name: {
        required,
        validName
      },
      phone: {
        async validPhone (string) {
          if (string) {
            const phoneNumber = await parsePhoneNumberFromString(string, 'GB')
            return phoneNumber && phoneNumber.isValid() && ['GG', 'JE', 'GB'].includes(phoneNumber.country)
          } else {
            return true
          }
        }
      },
      place: {},
      price1: {
        minValue: minValue(1),
        required
      },
      price5: {
        required
      },
      price10: {
        required
      }
    }
    if (this.price5 > 0) {
      validations = { ...validations, ...price5Validations }
    }
    if (this.price10 > 0) {
      validations = { ...validations, ...price10Validations }
    }
    if (this.price5 > 0 && this.price10 > 0) {
      validations = { ...validations, ...price5price10Validations }
    }
    return validations
  }
}
</script>

<style lang="sass" scoped>
.missing
  margin-bottom: 0.75rem
</style>
